/**
 * New Year banners — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_seasons`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const newYearBannersPack: ToilePack = {
    slug: 'new-year-banners',
    category: 'season',
    name: 'New Year banners',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 1216, h: 832},
    readings: [
        {key: 'pennants', label: 'Pennant banner', subject: 'A printable pennant banner of alternating flags on a string, each flag holding one letter of "HAPPY NEW YEAR", dashed cut lines'},
        {key: 'numbers', label: 'Number banner', subject: 'A sheet of large square flags each holding one bold digit from "0" to "9" to string together as the coming year, dashed cut lines'},
        {key: 'stars', label: 'Star swag', subject: 'A string of paper stars in three sizes to cut and hang, ending in one small tag star lettered exactly "CHEERS," dashed cut lines'},
    ],
    styles: [
        {key: 'glam', label: 'Black and gold', style: 'black-and-gold new year printable, confetti borders, elegant serif lettering, print-ready', palette: 'Black, gold and champagne cream', framing: 'Laid out on a dark page with a gold border and a margin'},
        {key: 'confetti', label: 'Confetti party', style: 'bright confetti party printable, streamers, rounded bold lettering, print-ready', palette: 'Gold, pink, teal and white', framing: 'Laid out on a vertical page with a confetti border and a white margin'},
        {key: 'minimal', label: 'Minimal', style: 'minimal new year printable, thin lines, generous white space, print-ready', palette: 'Black and gold on white', framing: 'Laid out on a clean page with wide margins'},
    ],
    directions: ['spelling a different year instead', 'for a milestone birthday instead', 'in a longer, wider swag'],
    directionHint: 'try: a milestone birthday instead',
    seo: {
        title: 'New Year Banners: Pennants, Numbers & Star Swag',
        metaDescription: 'Nine New Year banners — a pennant greeting, a number banner for the year, and a star swag — in black-and-gold, confetti, and minimal styles.',
        h1: 'New Year banners: pennants, numbers, and star swag',
        tagline: 'Nine sheets, three banner types, three styles each — dashed cut lines, ready to hang.',
        intro: 'Three New Year banner sheets — a pennant banner spelling the greeting, a number banner for the year, and a paper-star swag — each in black-and-gold, confetti-party, and minimal styles, with dashed cut lines throughout. Ask for a different greeting, a birthday version, or a longer string, and the same banner grammar carries it.',
        useCases: [
            "a New Year's Eve party backdrop",
            'a countdown mantel display',
            'a NYE photo-booth wall',
            'a family countdown celebration',
        ],
        faq: [
            {q: 'How do I hang these after printing?', a: 'Cut along the dashed lines and string the flags or numbers along ribbon or twine — no punched holes are included.'},
            {q: 'What size do these print at?', a: 'Each sheet is a single page; the pieces print at a size ready to cut by hand.'},
            {q: 'Can I get a different greeting or a birthday version?', a: 'Yes — the direction field takes any variation, including a different greeting or a birthday theme.'},
            {q: 'What are the three styles?', a: 'Black-and-gold with confetti borders, bright confetti-party, and minimal thin-line.'},
        ],
    },
}
